import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Audiotrack as AudioIcon,
  Delete as RemoveIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { deleteCaseMedia, inferMediaKind, uploadCaseMedia } from '../../../services/caseMedia';
import CaseMediaPlayer from '../../CaseMediaPlayer';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  publicBodyFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../caseManagerStyles';
import AnalyticsTagsField from '../AnalyticsTagsField';

const QuestionsStep = ({ data, onUpdate, caseId, uploadSessionId }) => {
  const [questionDialog, setQuestionDialog] = useState({
    open: false,
    questionIndex: -1,
    question: null
  });
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const createNewQuestion = (order) => ({
    id: `question_${Date.now()}`,
    question: '',
    correctAnswer: '',
    maxMarks: 2,
    markingInstructions: '',
    domainCodes: [],
    competencyCodes: [],
    safetyCritical: false,
    taxonomyVersion: '1',
    mediaFiles: [],
    order
  });

  const moveQuestionUp = (questionIndex) => {
    if (questionIndex === 0) return;

    const newQuestions = [...data.examinerQuestions];
    const temp = newQuestions[questionIndex];
    newQuestions[questionIndex] = newQuestions[questionIndex - 1];
    newQuestions[questionIndex - 1] = temp;

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const moveQuestionDown = (questionIndex) => {
    if (questionIndex === data.examinerQuestions.length - 1) return;

    const newQuestions = [...data.examinerQuestions];
    const temp = newQuestions[questionIndex];
    newQuestions[questionIndex] = newQuestions[questionIndex + 1];
    newQuestions[questionIndex + 1] = temp;

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const addQuestion = () => {
    const newQuestion = createNewQuestion(data.examinerQuestions.length);

    setQuestionDialog({
      open: true,
      questionIndex: data.examinerQuestions.length,
      question: { ...newQuestion }
    });
  };

  const editQuestion = (questionIndex) => {
    const question = data.examinerQuestions[questionIndex];
    setQuestionDialog({
      open: true,
      questionIndex,
      question: { ...question }
    });
  };

  const saveQuestion = () => {
    const { questionIndex, question } = questionDialog;

    // Validate required fields
    if (!question.question?.trim() || !question.correctAnswer?.trim() || !question.markingInstructions?.trim()) {
      setUploadError('Question, correct answer, and marking instructions are required');
      return;
    }

    let newQuestions = [...data.examinerQuestions];

    if (questionIndex === data.examinerQuestions.length) {
      // This is a new question, add it to the array
      question.order = newQuestions.length;
      newQuestions.push(question);
    } else {
      // This is an existing question, update it
      newQuestions[questionIndex] = question;
    }

    onUpdate({ examinerQuestions: newQuestions });
    setQuestionDialog({ open: false, questionIndex: -1, question: null });
    setUploadError('');
  };

  const deleteQuestion = (questionIndex) => {
    const newQuestions = data.examinerQuestions.filter((_, index) => index !== questionIndex);

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index
    }));

    onUpdate({ examinerQuestions: updatedQuestions });
  };

  const handleDialogClose = () => {
    // Don't save anything on cancel, just close
    setQuestionDialog({ open: false, questionIndex: -1, question: null });
    setUploadError('');
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setUploadError('');

    if (files.length === 0) return;

    const validFiles = [];
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/webp', 'image/gif',
      'video/mp4', 'video/quicktime', 'video/webm',
      'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/aac',
      'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/webm', 'audio/flac',
    ];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`File ${file.name} is not supported. Choose an image, video, or audio file.`);
        return;
      }
      const kind = inferMediaKind(file);
      const maxSize = kind === 'video' ? 500 * 1024 * 1024 : kind === 'audio' ? 100 * 1024 * 1024 : 20 * 1024 * 1024;
      if (file.size > maxSize) {
        setUploadError(`File ${file.name} is too large (max ${Math.round(maxSize / (1024 * 1024))} MB)`);
        return;
      }
      validFiles.push(file);
    }

    try {
      const uploadedFiles = [];
      setUploadStatus('Uploading');
      setUploadProgress(0);
      for (const file of validFiles) {
        const uploaded = await uploadCaseMedia({
          file,
          purpose: 'examiner-question',
          caseId,
          uploadSessionId,
          onProgress: setUploadProgress,
          onStatus: (next) => setUploadStatus(
            next.status === 'processing' || next.status === 'uploaded' ? 'Optimising' : next.status
          ),
        });
        uploadedFiles.push(uploaded);
      }

      const updatedQuestion = {
        ...questionDialog.question,
        mediaFiles: [...(questionDialog.question.mediaFiles || []), ...uploadedFiles]
      };

      setQuestionDialog({
        ...questionDialog,
        question: updatedQuestion
      });
      setUploadStatus('');

    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload files');
      setUploadStatus('');
    }

    // Clear the input
    event.target.value = '';
  };

  const removeFile = async (fileIndex) => {
    const removed = questionDialog.question.mediaFiles[fileIndex];
    const updatedFiles = questionDialog.question.mediaFiles.filter((_, index) => index !== fileIndex);
    setQuestionDialog({
      ...questionDialog,
      question: {
        ...questionDialog.question,
        mediaFiles: updatedFiles
      }
    });
    try {
      await deleteCaseMedia(removed);
    } catch {
      // Media already attached to a saved case is retained for history integrity.
    }
  };

  const mediaIcon = (file) => {
    const kind = inferMediaKind(file);
    if (kind === 'video') return <VideoIcon fontSize="small" />;
    if (kind === 'audio') return <AudioIcon fontSize="small" />;
    return <ImageIcon fontSize="small" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <CaseEditorStepFrame>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3, fontFamily: publicBodyFontFamily }}>
        Images, videos, and audio are optimised automatically for fast, consistent playback in the web and Unity apps.
      </Typography>

      {/* Option to add questions */}
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={3}>
        <Button
          startIcon={<AddIcon />}
          onClick={addQuestion}
          sx={publicPrimaryButtonSx}
        >
          Add Question
        </Button>
      </Box>

      {/* Questions List */}
      <Box>
        {data.examinerQuestions.map((question, questionIndex) => (
          <Card key={question.id} sx={{ ...caseManagerInsetSurfaceSx, mb: 2, borderRadius: '22px' }}>
            <CardContent>
              <Box display="flex" alignItems="flex-start">
                <Box flexGrow={1}>
                  <Typography variant="h6" gutterBottom>
                    Question {questionIndex + 1}
                  </Typography>

                  <Typography variant="body1" gutterBottom>
                    {question.question || 'No question text'}
                  </Typography>

                  <Box display="flex" gap={1} mb={1}>
                    <Chip
                      label={`${question.maxMarks} marks`}
                      size="small"
                      color="primary"
                    />
                    {question.mediaFiles && question.mediaFiles.length > 0 && (
                      <Chip
                        label={`${question.mediaFiles.length} media file(s)`}
                        size="small"
                        color="secondary"
                        icon={mediaIcon(question.mediaFiles[0])}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Correct Answer:</strong> {question.correctAnswer || 'No answer provided'}
                  </Typography>

                  {question.mediaFiles && question.mediaFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>Media Files:</Typography>
                      <Grid container spacing={1}>
                        {question.mediaFiles.map((file, fileIndex) => (
                          <Grid item key={fileIndex}>
                            <Chip icon={mediaIcon(file)} label={file.displayName || file.originalName || `Media ${fileIndex + 1}`} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => moveQuestionUp(questionIndex)}
                    disabled={questionIndex === 0}
                  >
                    <ArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveQuestionDown(questionIndex)}
                    disabled={questionIndex === data.examinerQuestions.length - 1}
                  >
                    <ArrowDownIcon />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => editQuestion(questionIndex)}
                sx={publicSecondaryButtonSx}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteQuestion(questionIndex)}
                sx={{ ...publicSecondaryButtonSx, borderColor: 'rgba(239,68,68,0.24)', color: '#b42318' }}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Empty State */}
      {data.examinerQuestions.length === 0 && (
        <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No examiner questions added
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can add questions that students will answer after completing the history taking session,
            or skip this section entirely.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQuestion}
            sx={publicSecondaryButtonSx}
          >
            Add Your First Question
          </Button>
        </Paper>
      )}

      {/* Question Edit Dialog */}
      <Dialog
        open={questionDialog.open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {questionDialog.questionIndex === data.examinerQuestions.length ? 'Add New Question' : 'Edit Question'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Question"
                value={questionDialog.question?.question || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, question: e.target.value }
                })}
                helperText="The question that will be presented to students"
                required
                error={!questionDialog.question?.question?.trim()}
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Correct Answer"
                value={questionDialog.question?.correctAnswer || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, correctAnswer: e.target.value }
                })}
                helperText="The expected correct answer"
                required
                error={!questionDialog.question?.correctAnswer?.trim()}
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Marks"
                value={questionDialog.question?.maxMarks || 2}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, maxMarks: parseInt(e.target.value) || 0 }
                })}
                inputProps={{ min: 0, max: 10 }}
                required
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Marking Instructions"
                value={questionDialog.question?.markingInstructions || ''}
                onChange={(e) => setQuestionDialog({
                  ...questionDialog,
                  question: { ...questionDialog.question, markingInstructions: e.target.value }
                })}
                helperText="Instructions for AI marking - how to award partial marks, common mistakes, etc."
                required
                error={!questionDialog.question?.markingInstructions?.trim()}
              />
            </Grid>

            <Grid item xs={12}>
              <AnalyticsTagsField
                domainCodes={questionDialog.question?.domainCodes || []}
                competencyCodes={questionDialog.question?.competencyCodes || []}
                customKind="domain"
                onChange={({ domainCodes, competencyCodes }) => setQuestionDialog({
                  ...questionDialog,
                  question: {
                    ...questionDialog.question,
                    domainCodes,
                    competencyCodes,
                  }
                })}
                helperText="Choose a stable domain or competency. Press Enter to keep a custom viva tag."
                sx={publicTextFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Media Files (Optional)
              </Typography>

              <input
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/flac"
                style={{ display: 'none' }}
                id="media-upload"
                multiple
                type="file"
                disabled={Boolean(uploadStatus)}
                onChange={handleFileUpload}
              />
              <label htmlFor="media-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={Boolean(uploadStatus)}
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload media
                </Button>
              </label>

              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Images up to 20 MB, audio up to 100 MB / 15 minutes, and MP4, MOV, or WebM video up to 500 MB / 20 minutes.
              </Typography>

              {uploadStatus && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption">{uploadStatus}…</Typography>
                    <Typography variant="caption">{uploadStatus === 'Uploading' ? `${uploadProgress}%` : 'Preparing playback'}</Typography>
                  </Stack>
                  <LinearProgress variant={uploadStatus === 'Uploading' ? 'determinate' : 'indeterminate'} value={uploadProgress} />
                </Box>
              )}

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {questionDialog.question?.mediaFiles && questionDialog.question.mediaFiles.length > 0 && (
                <Grid container spacing={2}>
                  {questionDialog.question.mediaFiles.map((file, fileIndex) => (
                    <Grid item xs={12} sm={6} md={4} key={file.assetId || file.id || fileIndex}>
                      <Card>
                        <CaseMediaPlayer media={file} compact sx={{ height: 150, objectFit: 'contain' }} />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" noWrap>
                            {file.displayName || file.originalName}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ p: 1, pt: 0 }}>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<RemoveIcon />}
                            onClick={() => removeFile(fileIndex)}
                          >
                            Remove
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={publicSecondaryButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={saveQuestion} 
            disabled={
              Boolean(uploadStatus) ||
              !questionDialog.question?.question?.trim() ||
              !questionDialog.question?.correctAnswer?.trim() ||
              !questionDialog.question?.markingInstructions?.trim()
            }
            sx={publicPrimaryButtonSx}
          >
            {questionDialog.questionIndex === data.examinerQuestions.length ? 'Add Question' : 'Save Question'}
          </Button>
        </DialogActions>
      </Dialog>
    </CaseEditorStepFrame>
  );
};

export default QuestionsStep;
